import { useEffect } from 'react';
import { EWalletId } from '@ankr.com/provider';
import Box from '@mui/material/Box';
import MuiModal, { ModalProps } from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { useQueryParam } from 'use-query-params';
import { isMobile } from 'web3modal';

import { Close, useHeaderHeight } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { useConnectMutation } from 'modules/store';

import { BinanceWallet } from './BinanceWallet';
import { METAMASK_WALLET_ID, MetamaskWallet } from './MetamaskWallet';
import { TRUST_WALLET_ID, TrustWallet } from './TrustWallet';
import { WalletButton } from './WalletButton';
import translation from './walletModal.translation';

interface Props extends Omit<ModalProps, 'children'> {
  onClose: () => void;
}

const BASE_WALLETS: EWalletId[] = [
  EWalletId.walletconnect,
  EWalletId.imtoken,
  EWalletId.math,
  EWalletId.huobi,
];

const WALLET_PARAM_ID = 'wallet';

export function WalletModal(props: Props): JSX.Element {
  const [wallet, setWallet] = useQueryParam<string | undefined>(
    WALLET_PARAM_ID,
  );
  const headerHeight = useHeaderHeight();
  const { classes } = useStyles({ headerHeight });
  const { t, keys } = useTranslation(translation);
  const { onClose } = props;

  const [connect, { isLoading }] = useConnectMutation();

  const handleConnect = async (walletId: EWalletId): Promise<void> => {
    const result = await connect(walletId);
    if ('error' in result) {
      return;
    }

    onClose();
  };

  useEffect(() => {
    if (!isMobile() || !wallet) {
      return;
    }

    switch (wallet) {
      case METAMASK_WALLET_ID:
      case TRUST_WALLET_ID:
      case EWalletId.coinbase:
        void connect(wallet);
        break;
      default:
        break;
    }

    setWallet(undefined);
  }, [wallet, setWallet, connect]);

  return (
    <MuiModal {...props}>
      <div onClick={onClose} className={classes.area}>
        <div
          className={classes.container}
          onClick={event => event.stopPropagation()}
          data-tid="auth-wallet-modal"
        >
          <Close
            className={classes.close}
            onClose={onClose}
            data-tid="auth-wallet-modal-close-button"
          />

          <Typography variant="h6" textAlign="center" marginBottom="50px">
            {t(keys.connectWalletToContinue)}
          </Typography>

          <Box display="flex" flexDirection="column" gap={4}>
            <MetamaskWallet onConnect={handleConnect} loading={isLoading} />

            <TrustWallet onConnect={handleConnect} loading={isLoading} />

            <BinanceWallet onConnect={handleConnect} loading={isLoading} />

            {BASE_WALLETS.map(walletId => (
              <WalletButton
                key={walletId}
                walletId={walletId}
                onClick={() => handleConnect(walletId)}
                disabled={isLoading}
              />
            ))}
          </Box>
        </div>
      </div>
    </MuiModal>
  );
}

const useStyles = makeStyles<{ headerHeight: number }>()(
  (theme, { headerHeight }) => ({
    area: {
      padding: 0,
      background: 'none',
      border: 'none',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100vh',
      overflowY: 'scroll',
      paddingTop: headerHeight,

      [theme.breakpoints.down('md')]: {
        paddingTop: 0,
        alignItems: 'flex-start',
      },
    },

    container: {
      background: 'none',
      border: 'none',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 580,
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(4),
      width: '100%',
      padding: theme.spacing(15),
      height: 'max-content',
      margin: `${headerHeight}px 0`,

      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(15, 8),
        margin: 0,
        borderRadius: 0,
        maxWidth: '100%',
        minHeight: '100vh',
      },

      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(15, 4),
      },
    },

    close: {
      position: 'absolute',
      top: 32,
      right: 32,
    },
  }),
);
